import "./AbilitiesTab.css";
import React from "react";
import UiButton from "../../../components/Buttons/UiButton";
import { calcAbilityModFromTotal, getAbilityTotal } from "../characterHelpers";
import { IRpgStat } from "../../../models/IRpgStat";
import AbilityRow from "../../../components/Character/AbilityStats/AbilityRow";
import { CharacterPortrait } from "../../../components/Character/CharacterImage/CharacterPortrait";
import { ICharacterPortraitProps } from "../../../models/ICharacter";

interface IAbilitiesTabProps {
    isReadOnly: boolean;
    pointsRemaining: number;
    statsReady: boolean;
    abilityScores: IRpgStat[];
    portraitProps: ICharacterPortraitProps;

    onRollAllScores: () => void;
    onResetPointBuy: () => void;
    onPointBuyAdjust: (abilityName: string, delta: -1 | 1) => void;
    canPointBuyAdjust: (abilityName: string, delta: -1 | 1) => boolean;
    getPointBuyCostForScore: (score: number) => number;
}

const AbilitiesTab: React.FC<IAbilitiesTabProps> = ({
    isReadOnly,
    pointsRemaining,
    statsReady,
    abilityScores,
    portraitProps,
    onRollAllScores,
    onResetPointBuy,
    onPointBuyAdjust,
    canPointBuyAdjust,
    getPointBuyCostForScore,
}) => {
    return (
        <div className="abilities-root">
            <div className="abilities-layout">
                <div className="abilities-portraitPane">
                    <div className="abilities-portraitSticky">
                        <CharacterPortrait {...portraitProps} />
                    </div>
                </div>

                <div className="abilities-page">
                    <div className="abilities-strip">
                        <div className="abilities-stripLeft">ROLL FOR SCORES</div>
                        <div className="abilities-stripRight">POINT BUY</div>
                    </div>

                    <div className="abilities-topRow">
                        <div className="abilities-topLeft">
                            <UiButton
                                variant="sage"
                                text="Roll Ability Scores"
                                iconAlt="Dice"
                                iconSrc="/images/Items/item_dice.png"
                                disabled={isReadOnly}
                                onClickCallback={onRollAllScores}
                            />
                        </div>

                        <div className="abilities-topRight">
                            <div className="abilities-points">
                                <span className="abilities-pointsLabel">Points Remaining:</span>
                                <span className="abilities-pointsValue">{pointsRemaining}</span>
                            </div>
                            <UiButton
                                variant="sage"
                                text="Reset"
                                disabled={isReadOnly}
                                onClickCallback={onResetPointBuy}
                            />
                        </div>
                    </div>

                    <div className="abilities-rows">
                        {abilityScores.map((stat) => {
                            const total = getAbilityTotal(stat);
                            const mod = calcAbilityModFromTotal(total);

                            return (
                                <AbilityRow
                                    key={stat.Name}
                                    name={stat.Name}
                                    score={total}
                                    mod={mod}
                                    pointBuyCost={getPointBuyCostForScore(stat.Value)}
                                    canDec={!isReadOnly && canPointBuyAdjust(stat.Name, -1)}
                                    canInc={!isReadOnly && canPointBuyAdjust(stat.Name, 1)}
                                    onDec={() => {
                                        if (isReadOnly) { return; }
                                        onPointBuyAdjust(stat.Name, -1);
                                    }}
                                    onInc={() => {
                                        if (isReadOnly) { return; }
                                        onPointBuyAdjust(stat.Name, 1);
                                    }}
                                />
                            );
                        })}
                    </div>

                    {!statsReady && (
                        <div className="abilities-hint">
                            Adjust ability scores (Points Remaining must be less than 1) to unlock the other tabs.<br/>
                            Roll again or use the Point Buy buttons.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AbilitiesTab;
